// Crosstalk Mobile — product-match quiz
// Step machine + recommendation engine. To change/extend the logic,
// edit the recommend() switch below; everything else is generic.
(function () {
  'use strict';

  const root = document.querySelector('[data-cm-quiz]');
  if (!root) return;

  // Question step IDs in order — must match data-cm-quiz-step-id attrs
  const QUESTIONS = ['location', 'fiveg', 'wans', 'dualmodem'];
  const TOTAL = QUESTIONS.length;

  const productsScript = document.querySelector('[data-cm-quiz-products]');
  let products = {};
  try {
    products = JSON.parse(productsScript.textContent.trim());
  } catch (e) {
    console.error('[cm-quiz] could not parse product data', e);
  }

  const stepEl = root.querySelector('[data-cm-quiz-step]');
  const totalEl = root.querySelector('[data-cm-quiz-total]');
  const labelEl = root.querySelector('[data-cm-quiz-label]');
  const fillEl = root.querySelector('[data-cm-quiz-fill]');
  const progressEl = root.querySelector('[data-cm-quiz-progress]');
  const navEl = root.querySelector('[data-cm-quiz-nav]');
  const backBtn = root.querySelector('[data-cm-quiz-back]');
  const restartBtn = root.querySelector('[data-cm-quiz-restart]');
  const stepEls = Array.from(root.querySelectorAll('[data-cm-quiz-step-id]'));

  totalEl.textContent = String(TOTAL);

  // currentIndex: -1 = intro, 0..TOTAL-1 = question, TOTAL = result
  const state = {
    currentIndex: -1,
    answers: {}, // { location: 'mobile', fiveg: 'yes', wans: '2', dualmodem: 'no' }
  };

  function currentStepId() {
    if (state.currentIndex < 0) return 'intro';
    return state.currentIndex < TOTAL ? QUESTIONS[state.currentIndex] : 'result';
  }

  function activate(stepId) {
    stepEls.forEach((el) => {
      if (el.dataset.cmQuizStepId === stepId) {
        el.setAttribute('data-cm-quiz-active', '');
      } else {
        el.removeAttribute('data-cm-quiz-active');
      }
    });
  }

  function updateProgress() {
    const isIntro = state.currentIndex < 0;
    const isResult = state.currentIndex >= TOTAL;
    const stepNum = isResult ? TOTAL : Math.max(state.currentIndex + 1, 1);
    stepEl.textContent = String(stepNum);
    labelEl.textContent = isResult ? 'Result' : 'Question';
    fillEl.style.width = `${(stepNum / TOTAL) * 100}%`;
    // Progress bar visible only during questions
    progressEl.toggleAttribute('hidden', isIntro || isResult);
    // Hide entire nav row on intro (no buttons should appear there)
    navEl.toggleAttribute('hidden', isIntro);
    // Back: hidden on intro+result, disabled on Q1, visible from Q2+
    backBtn.toggleAttribute('hidden', isIntro || isResult);
    backBtn.disabled = isIntro || state.currentIndex <= 0 || isResult;
    // Restart: only on result
    restartBtn.toggleAttribute('hidden', !isResult);
  }

  function start() {
    state.currentIndex = 0;
    activate(currentStepId());
    updateProgress();
  }

  function selectOption(question, value) {
    state.answers[question] = value;
    // Visually mark the chosen option
    root.querySelectorAll(`[data-cm-quiz-option][data-question="${question}"]`).forEach((opt) => {
      opt.setAttribute('aria-selected', opt.dataset.value === value ? 'true' : 'false');
    });
    // Auto-advance after a brief delay
    setTimeout(() => {
      if (state.currentIndex < TOTAL - 1) {
        state.currentIndex += 1;
        activate(currentStepId());
        updateProgress();
      } else {
        // Last question answered — show result
        state.currentIndex = TOTAL;
        showResult();
        updateProgress();
      }
    }, 200);
  }

  function back() {
    if (state.currentIndex === 0) return;
    state.currentIndex -= 1;
    activate(currentStepId());
    updateProgress();
  }

  function restart() {
    state.currentIndex = -1;
    state.answers = {};
    root.querySelectorAll('[data-cm-quiz-option]').forEach((opt) => {
      opt.setAttribute('aria-selected', 'false');
    });
    activate(currentStepId());
    updateProgress();
  }

  // ── Recommendation engine ─────────────────────
  // Extend by adding cases below. Each branch returns { handle, reasons[] }.
  function recommend(answers) {
    const wants_dual = answers.dualmodem === 'yes';
    const fiveg_primary = answers.fiveg === 'yes';
    const loc = answers.location;
    const wans = parseInt(answers.wans || '0', 10);

    // Dual modem trumps everything — only the BR2 Pro 5G has two modems
    if (wants_dual) {
      return {
        handle: 'max-br2-pro-5g',
        reasons: [
          'Dual built-in 5G modems — bond two carriers at once for faster speeds and seamless failover',
          loc === 'mobile' || loc === 'field'
            ? 'Rugged build for mobile and field deployments'
            : 'Multi-WAN ports for wired uplinks alongside cellular',
          'Mission-critical reliability when downtime is not an option',
        ],
      };
    }

    // Mobile or field, 5G primary → MAX BR1 Pro 5G
    if ((loc === 'mobile' || loc === 'field') && fiveg_primary) {
      return {
        handle: 'max-br1-pro-5g',
        reasons: [
          'Built for mobile + field deployments — rugged housing, vehicle power options',
          '5G primary with WAN ports for wired backup or hybrid uplinks',
          wans >= 2
            ? `Handles up to ${wans}+ wired WAN sources alongside cellular`
            : 'Single 5G modem keeps the form factor compact',
        ],
      };
    }

    // Mobile or field, 5G NOT primary → MAX BR1 Mini (smaller, simpler)
    if (loc === 'mobile' || loc === 'field') {
      return {
        handle: 'max-br1-mini-with-wi-fi',
        reasons: [
          'Compact rugged router for mobile installs where wired is the primary uplink',
          '5G as backup / failover when wired drops',
          'Built-in Wi-Fi keeps the install footprint small',
        ],
      };
    }

    // Office or home, NO 5G + multiple WANs → Balance 310 Prime
    // Pure multi-WAN bonding router; no cellular.
    if ((loc === 'office' || loc === 'home') && !fiveg_primary && wans >= 2) {
      return {
        handle: 'balance-310-prime',
        reasons: [
          'Pure multi-WAN router — no cellular hardware, just fiber/cable/DSL bonding',
          `1 Gbps SpeedFusion across your ${wans >= 4 ? '4+' : wans} wired uplinks for the highest aggregate throughput`,
          'Right call when 5G is not part of your setup and you need every Mbps your wired lines can provide',
        ],
      };
    }

    // Office or home, 5G primary + multiple WANs → Balance 310 Fiber 5G
    // Single 5G modem, heavy routing throughput, 600 Mbps SpeedFusion.
    if ((loc === 'office' || loc === 'home') && fiveg_primary && wans >= 2) {
      return {
        handle: 'balance-310-fiber-5g-bpl-310-fbr-5gh-t-prm',
        reasons: [
          '5G plus multiple wired WANs on one device — built for offices that need both',
          `600 Mbps SpeedFusion bonding across cellular + ${wans >= 4 ? '4+' : wans} wired uplinks`,
          'Heavy routing throughput for high-traffic deployments — handles every Mbps your network can throw at it',
        ],
      };
    }

    // Office or home, simpler setups → B One 5G
    // (multi-WAN desktop, dual WAN + USB WAN, Wi-Fi 6, X62 modem)
    if (loc === 'office' || loc === 'home') {
      return {
        handle: 'b-one-5g',
        reasons: [
          loc === 'office'
            ? 'Desktop form factor — sits on a shelf or rack instead of bolted in a vehicle'
            : 'Quiet desktop housing — designed for residential or small-office settings',
          fiveg_primary
            ? '5G primary with multiple WAN ports for hybrid uplinks'
            : `Two WAN ports + USB WAN cover up to ${Math.max(wans, 1)} wired uplinks; 5G as backup`,
          'Wi-Fi 6 + X62 modem keeps the network fast and current',
        ],
      };
    }

    // Fallback
    return {
      handle: 'max-br1-pro-5g',
      reasons: [
        'A flexible all-rounder that handles mobile, field, and office deployments',
        '5G primary with WAN port for wired backup',
        'Rugged enough for field, compact enough for office',
      ],
    };
  }

  function showResult() {
    activate('result');
    const rec = recommend(state.answers);
    const product = products[rec.handle];

    if (!product) {
      console.warn('[cm-quiz] recommended handle not in product pool:', rec.handle);
      return;
    }

    const titleEl = root.querySelector('[data-cm-result-title]');
    const priceEl = root.querySelector('[data-cm-result-price]');
    const imageEl = root.querySelector('[data-cm-result-image]');
    const reasonsEl = root.querySelector('[data-cm-result-reasons]');
    const linkEl = root.querySelector('[data-cm-result-link]');

    titleEl.textContent = product.title;
    priceEl.textContent = formatMoneyUSD(product.price);
    imageEl.src = product.image || '';
    imageEl.alt = product.title;
    linkEl.href = product.url;

    reasonsEl.innerHTML = '';
    rec.reasons.forEach((r) => {
      const li = document.createElement('li');
      li.textContent = r;
      reasonsEl.appendChild(li);
    });
  }

  function formatMoneyUSD(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // ── Wire events ───────────────────────────────
  root.addEventListener('click', (e) => {
    if (e.target.closest('[data-cm-quiz-start]')) { start(); return; }
    const opt = e.target.closest('[data-cm-quiz-option]');
    if (opt) {
      selectOption(opt.dataset.question, opt.dataset.value);
      return;
    }
    if (e.target.closest('[data-cm-quiz-back]')) back();
    if (e.target.closest('[data-cm-quiz-restart]')) restart();
  });

  // Initial state
  updateProgress();
})();
